import React from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { CardBrandType } from '../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import {
  CreditCardClearanceStatus,
  CreditCardClearanceType,
} from '../../../../../api/types/payment-gateway/credit-card-clearance-entity';
import {
  CreditCardClarenceFilterType,
  defaultCreditCardClarenceFilter,
} from '../credit-card-clarence-filter.type';

interface CreditCardClarenceFilterProps {
  filters: CreditCardClarenceFilterType;
  loading?: boolean;
  onChange: (filters: CreditCardClarenceFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

const cardBrandOptions = [
  { label: 'Visa', value: CardBrandType.VISA },
  { label: 'Mastercard', value: CardBrandType.MASTERCARD },
  { label: 'Isracard', value: CardBrandType.ISRACARD },
  { label: 'American Express', value: CardBrandType.AMERICAN_EXPRESS },
  { label: 'Diners Club', value: CardBrandType.DINERS_CLUB },
  { label: 'Discover', value: CardBrandType.DISCOVER },
  { label: 'JCB', value: CardBrandType.JCB },
  { label: 'Maestro', value: CardBrandType.MAESTRO },
  { label: 'כרטיס פרטי', value: CardBrandType.PRIVATE_LABEL },
];

const clearanceStatusOptions = [
  { label: 'הצליח', value: CreditCardClearanceStatus.SUCCESS },
  { label: 'נכשל', value: CreditCardClearanceStatus.FAILED },
  { label: 'ממתין', value: CreditCardClearanceStatus.PENDING },
];

const clearanceTypeOptions = [
  { label: 'רגיל', value: CreditCardClearanceType.REGULAR },
  { label: 'תשלומים', value: CreditCardClearanceType.INSTALLMENT },
];

export const CreditCardClarenceFilter: React.FC<
  CreditCardClarenceFilterProps
> = ({ filters, loading, onChange, onSearch, onClear }) => {
  const update = <K extends keyof CreditCardClarenceFilterType>(
    key: K,
    value: CreditCardClarenceFilterType[K],
  ) => {
    onChange({ ...filters, [key]: value });
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    onChange(defaultCreditCardClarenceFilter());
  };

  const styles = {
    container: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '1.1rem',
      fontWeight: 600 as const,
      color: '#333',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
      gap: '12px',
    },
    field: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    label: {
      fontSize: '0.8rem',
      color: '#555',
    },
    inputFull: {
      width: '100%',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '12px',
      borderTop: '1px solid #eee',
      paddingTop: '12px',
    },
    buttonsRow: {
      display: 'flex',
      gap: '8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>סינונים</div>
      </div>

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label}>מתאריך</label>
          <Calendar
            value={filters.startDate}
            onChange={(e) => update('startDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="מתאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>עד תאריך</label>
          <Calendar
            value={filters.endDate}
            onChange={(e) => update('endDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="עד תאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>חברת אשראי</label>
          <MultiSelect
            value={filters.cardBrand}
            options={cardBrandOptions}
            onChange={(e) => update('cardBrand', e.value ?? [])}
            placeholder="הכל"
            display="chip"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>סטטוס סליקה</label>
          <MultiSelect
            value={filters.creditCardClearanceStatuses}
            options={clearanceStatusOptions}
            onChange={(e) =>
              update('creditCardClearanceStatuses', e.value ?? [])
            }
            placeholder="הכל"
            display="chip"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>סוג סליקה</label>
          <MultiSelect
            value={filters.creditCardClearanceType}
            options={clearanceTypeOptions}
            onChange={(e) => update('creditCardClearanceType', e.value ?? [])}
            placeholder="הכל"
            display="chip"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>קוד סטטוס</label>
          <InputText
            value={filters.statusCode ?? ''}
            onChange={(e) =>
              update(
                'statusCode',
                e.target.value === '' ? null : e.target.value,
              )
            }
            maxLength={50}
            placeholder="קוד סטטוס"
            style={styles.inputFull}
          />
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.buttonsRow}>
          <Button
            icon="pi pi-filter-slash"
            label="נקה"
            text
            severity="secondary"
            onClick={handleClear}
            disabled={loading}
          />
          <Button
            icon="pi pi-search"
            label="חפש"
            onClick={onSearch}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
